import { call, put, select, takeEvery, all } from 'redux-saga/effects'
import { notification } from 'antd'
import * as api from './api'
import * as actions from './actions'
import * as selectors from './selectors'

function* fetchOffersWorker({ payload = {} }) {
  try {
    yield put(actions.setLoading())

    const perPage = yield select(selectors.getPerPage)

    const {
      page = 1,
      search = '',
      network = '',
      programStatus = '',
      approvalStatus = '',
      sort = '',
      offerType: payloadOfferType,
    } = payload

    // Use offerType from payload if provided (for tab changes), otherwise read from state
    let offerType = payloadOfferType
    if (!offerType) {
      const activeTab = yield select(selectors.getActiveTab)
      offerType = activeTab === 'game' ? 'game' : 'app'
    }

    yield put(actions.saveFilters({ page, search, network, programStatus, approvalStatus, sort }))

    const response = yield call(api.getEarnOffers, {
      page,
      perPage,
      search,
      network,
      programStatus,
      approvalStatus: offerType === 'app' ? approvalStatus : undefined,
      sort,
      offerType,
    })

    if (response && response.data) {
      yield put(actions.fetchOffersSuccess({
        data: response.data.data || [],
        page: page,
        pageCount: response.data.page_count || 1,
        total: response.data.total || 0,
      }))
    }
  } catch (error) {
    yield put(actions.fetchOffersFailure())
    notification.error({
      message: 'Error fetching earn offers',
      description: error.message || 'Failed to load offers',
    })
  }
}

function* fetchNetworksWorker() {
  try {
    const response = yield call(api.getEarnNetworks)
    if (response && response.data) {
      yield put(actions.setNetworks(response.data.data || []))
    }
  } catch (error) {
  }
}

function* fetchGameGenresWorker() {
  try {
    // /admin/v1/game-genres returns a bare array of { value, label }
    const response = yield call(api.getGameGenres)
    if (response && Array.isArray(response.data)) {
      yield put(actions.setGameGenres(response.data))
    }
  } catch (error) {
  }
}

function* refetchCurrentPage() {
  const page = yield select(selectors.getPage)
  const search = yield select(selectors.getSearch)
  const network = yield select(selectors.getNetwork)
  const programStatus = yield select(selectors.getProgramStatus)
  const approvalStatus = yield select(selectors.getApprovalStatus)
  const sort = yield select(selectors.getSort)
  yield put(actions.fetchOffers({ page, search, network, programStatus, approvalStatus, sort }))
}

function* handleBulkResponse(response, successKey, actionName) {
  const successIds = response.data[successKey] || []
  const errors = response.data.errors || []

  if (successIds.length > 0) {
    notification.success({
      message: `${actionName} successful`,
      description: `${successIds.length} offer(s) ${actionName.toLowerCase()}`,
      duration: 4,
      placement: 'bottom',
    })
  }

  errors.forEach(err => {
    notification.error({
      message: `Failed to ${actionName.toLowerCase()} offer #${err.id}`,
      description: (err.errors || []).join(', '),
      duration: 4,
      placement: 'bottom',
    })
  })

  yield put(actions.setSelectedRowKeys([]))
  yield* refetchCurrentPage()
}

function* bulkApproveWorker({ payload }) {
  try {
    yield put(actions.setBulkActionLoading(true))
    const response = yield call(api.bulkApproveOffers, payload.ids)
    if (response && response.data) {
      yield* handleBulkResponse(response, 'approved', 'Approved')
    }
  } catch (error) {
    notification.error({ message: 'Error approving offers', description: error.message })
  } finally {
    yield put(actions.setBulkActionLoading(false))
  }
}

function* bulkDeclineWorker({ payload }) {
  try {
    yield put(actions.setBulkActionLoading(true))
    const response = yield call(api.bulkDeclineOffers, payload.ids)
    if (response && response.data) {
      yield* handleBulkResponse(response, 'declined', 'Declined')
    }
  } catch (error) {
    notification.error({ message: 'Error declining offers', description: error.message })
  } finally {
    yield put(actions.setBulkActionLoading(false))
  }
}

function* bulkUndoWorker({ payload }) {
  try {
    yield put(actions.setBulkActionLoading(true))
    const response = yield call(api.bulkUndoOffers, payload.ids)
    if (response && response.data) {
      yield* handleBulkResponse(response, 'undone', 'Undone')
    }
  } catch (error) {
    notification.error({ message: 'Error undoing offers', description: error.message })
  } finally {
    yield put(actions.setBulkActionLoading(false))
  }
}

function* fetchNetworkProgramWorker({ payload }) {
  try {
    yield put(actions.setProgramLoading(true))
    yield put(actions.setProgramDetail(null))
    const response = yield call(api.getNetworkProgram, payload.id)
    if (response && response.data) {
      yield put(actions.setProgramDetail(response.data))
    } else {
      yield put(actions.setProgramLoading(false))
    }
  } catch (error) {
    yield put(actions.setProgramLoading(false))
  }
}

function* createNetworkProgramWorker({ payload }) {
  try {
    yield put(actions.setProgramSaving(true))
    const response = yield call(api.createNetworkProgram, payload.data)
    if (response && response.data) {
      notification.success({
        message: 'Program created',
        duration: 4,
        placement: 'bottom',
      })
      if (payload.callback) payload.callback()
      yield* refetchCurrentPage()
    }
  } catch (error) {
    // parseError already surfaced the message
  } finally {
    yield put(actions.setProgramSaving(false))
  }
}

function* updateNetworkProgramWorker({ payload }) {
  try {
    yield put(actions.setProgramSaving(true))
    const response = yield call(api.updateNetworkProgram, {
      id: payload.id,
      data: payload.data,
    })
    if (response && response.data) {
      notification.success({
        message: 'Program updated',
        duration: 4,
        placement: 'bottom',
      })
      if (payload.callback) payload.callback()
      yield* refetchCurrentPage()
    }
  } catch (error) {
    // parseError already surfaced the message
  } finally {
    yield put(actions.setProgramSaving(false))
  }
}

function* deleteNetworkProgramWorker({ payload }) {
  try {
    yield put(actions.setProgramSaving(true))
    yield call(api.deleteNetworkProgram, payload.id)
    notification.success({
      message: 'Program deleted',
      duration: 4,
      placement: 'bottom',
    })
    if (payload.callback) payload.callback()
    yield* refetchCurrentPage()
  } catch (error) {
    // parseError already surfaced the message
  } finally {
    yield put(actions.setProgramSaving(false))
  }
}

function* fetchNetworkProgramGoalWorker({ payload }) {
  try {
    yield put(actions.setGoalLoading(true))
    yield put(actions.setGoalDetail(null))
    const response = yield call(api.getNetworkProgramGoal, payload.id)
    if (response && response.data) {
      yield put(actions.setGoalDetail(response.data))
    } else {
      yield put(actions.setGoalLoading(false))
    }
  } catch (error) {
    yield put(actions.setGoalLoading(false))
  }
}

function* createNetworkProgramGoalWorker({ payload }) {
  try {
    yield put(actions.setGoalSaving(true))
    const response = yield call(api.createNetworkProgramGoal, payload.data)
    if (response && response.data) {
      notification.success({
        message: 'Goal created',
        duration: 4,
        placement: 'bottom',
      })
      if (payload.callback) payload.callback()
      yield* refetchCurrentPage()
    }
  } catch (error) {
    // parseError already surfaced the message
  } finally {
    yield put(actions.setGoalSaving(false))
  }
}

function* updateNetworkProgramGoalWorker({ payload }) {
  try {
    yield put(actions.setGoalSaving(true))
    const response = yield call(api.updateNetworkProgramGoal, {
      id: payload.id,
      data: payload.data,
    })
    if (response && response.data) {
      notification.success({
        message: 'Goal updated',
        duration: 4,
        placement: 'bottom',
      })
      if (payload.callback) payload.callback()
      yield* refetchCurrentPage()
    }
  } catch (error) {
    // parseError already surfaced the message
  } finally {
    yield put(actions.setGoalSaving(false))
  }
}

function* deleteNetworkProgramGoalWorker({ payload }) {
  try {
    yield put(actions.setGoalSaving(true))
    yield call(api.deleteNetworkProgramGoal, payload.id)
    notification.success({
      message: 'Goal deleted',
      duration: 4,
      placement: 'bottom',
    })
    if (payload.callback) payload.callback()
    yield* refetchCurrentPage()
  } catch (error) {
    // parseError already surfaced the message
  } finally {
    yield put(actions.setGoalSaving(false))
  }
}

// Refresh the open program's detail (which carries `features`) without nulling it
// first, so the Features tab updates in place rather than flashing the load guard.
function* refreshProgramDetail(programId) {
  const response = yield call(api.getNetworkProgram, programId)
  if (response && response.data) {
    yield put(actions.setProgramDetail(response.data))
  }
}

function* createNetworkProgramFeatureWorker({ payload }) {
  try {
    yield put(actions.setFeatureSaving(true))
    yield call(api.createNetworkProgramFeature, payload.data)
    notification.success({
      message: 'Genre added',
      duration: 4,
      placement: 'bottom',
    })
    yield* refreshProgramDetail(payload.networkProgramId)
  } catch (error) {
    // parseError already surfaced the message
  } finally {
    yield put(actions.setFeatureSaving(false))
  }
}

function* deleteNetworkProgramFeatureWorker({ payload }) {
  try {
    yield put(actions.setFeatureSaving(true))
    yield call(api.deleteNetworkProgramFeature, payload.id)
    notification.success({
      message: 'Genre removed',
      duration: 4,
      placement: 'bottom',
    })
    yield* refreshProgramDetail(payload.networkProgramId)
  } catch (error) {
    // parseError already surfaced the message
  } finally {
    yield put(actions.setFeatureSaving(false))
  }
}

export default function* earnOffersManagementSaga() {
  yield all([
    takeEvery(actions.fetchOffers.type, fetchOffersWorker),
    takeEvery(actions.fetchNetworks.type, fetchNetworksWorker),
    takeEvery(actions.fetchGameGenres.type, fetchGameGenresWorker),
    takeEvery(actions.bulkApprove.type, bulkApproveWorker),
    takeEvery(actions.bulkDecline.type, bulkDeclineWorker),
    takeEvery(actions.bulkUndo.type, bulkUndoWorker),
    takeEvery(actions.fetchNetworkProgram.type, fetchNetworkProgramWorker),
    takeEvery(actions.createNetworkProgram.type, createNetworkProgramWorker),
    takeEvery(actions.updateNetworkProgram.type, updateNetworkProgramWorker),
    takeEvery(actions.deleteNetworkProgram.type, deleteNetworkProgramWorker),
    takeEvery(actions.fetchNetworkProgramGoal.type, fetchNetworkProgramGoalWorker),
    takeEvery(actions.createNetworkProgramGoal.type, createNetworkProgramGoalWorker),
    takeEvery(actions.updateNetworkProgramGoal.type, updateNetworkProgramGoalWorker),
    takeEvery(actions.deleteNetworkProgramGoal.type, deleteNetworkProgramGoalWorker),
    takeEvery(actions.createNetworkProgramFeature.type, createNetworkProgramFeatureWorker),
    takeEvery(actions.deleteNetworkProgramFeature.type, deleteNetworkProgramFeatureWorker),
  ])
}
